import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, Gem, HandCoins, Wallet, BellRing, BarChart3, Settings, LogOut, Vault, Gavel, Warehouse, Landmark, ScrollText, MessageSquareText,
} from "lucide-react";
import { useAuth, isManagerUp } from "../lib/auth";
import { useLang } from "../lib/i18n";
import { API } from "../lib/api";
import { LanguageSwitcher } from "./LanguageSwitcher";

export function ShopLayout() {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [alertCount, setAlertCount] = useState(0);
  const shop = user?.shop;
  const active = shop?.status === "active";

  useEffect(() => {
    if (active) {
      API.get("/shop/alerts").then((r) => setAlertCount(r.data.count)).catch(() => {});
    }
  }, [active]);

  useEffect(() => {
    document.documentElement.style.setProperty("--brand", user?.shop?.brand_color || "#1E3A5F");
  }, [user?.shop?.brand_color]);

  const links = [
    { to: "/app", icon: LayoutDashboard, label: t("nav.dashboard"), end: true },
    { to: "/app/customers", icon: Users, label: t("nav.customers") },
    { to: "/app/loans", icon: HandCoins, label: t("nav.loans") },
    { to: "/app/items", icon: Gem, label: t("nav.items") },
    { to: "/app/repayments", icon: Wallet, label: t("nav.repayments") },
    { to: "/app/finance", icon: Landmark, label: t("nav.finance") },
    { to: "/app/auctions", icon: Gavel, label: t("nav.auctions") },
    { to: "/app/warehouse", icon: Warehouse, label: t("nav.warehouse") },
    { to: "/app/reports", icon: BarChart3, label: t("nav.reports") },
    { to: "/app/messages", icon: MessageSquareText, label: t("nav.messages") },
    ...(isManagerUp(user) ? [{ to: "/app/audit", icon: ScrollText, label: t("nav.audit") }] : []),
    { to: "/app/settings", icon: Settings, label: t("nav.settings") },
  ];

  return (
    <div className="min-h-screen flex bg-background" data-testid="shop-layout">
      <aside className="w-60 shrink-0 text-slate-200 hidden lg:flex flex-col fixed inset-y-0 z-30"
        style={{ backgroundColor: "color-mix(in srgb, var(--brand, #1E3A5F) 30%, #0f172a)" }}>
        <div className="px-5 py-5 flex items-center gap-2.5 border-b border-slate-700/60">
          <Vault className="h-7 w-7 text-emerald-400" />
          <div>
            <div className="font-heading font-700 font-bold text-white leading-tight">{t("brand")}</div>
            <div className="text-xs text-slate-400 truncate max-w-[140px]" data-testid="shop-name-label">{shop?.name}</div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {active &&
            links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                data-testid={`nav-${l.to.replace("/app", "").replace("/", "") || "dashboard"}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    isActive ? "bg-emerald-600 text-white" : "text-slate-300 hover:bg-slate-700/60 hover:text-white"
                  }`
                }
              >
                <l.icon className="h-4.5 w-4.5 h-[18px] w-[18px]" />
                {l.label}
              </NavLink>
            ))}
          {!active && (
            <NavLink
              to="/app/settings"
              data-testid="nav-settings"
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium ${
                  isActive ? "bg-emerald-600 text-white" : "text-slate-300 hover:bg-slate-700/60"
                }`
              }
            >
              <Settings className="h-[18px] w-[18px]" />
              {t("nav.settings")}
            </NavLink>
          )}
        </nav>
        <button
          data-testid="logout-button"
          onClick={async () => { await logout(); navigate("/login"); }}
          className="m-3 flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-slate-300 hover:bg-slate-700/60 hover:text-white transition-colors"
        >
          <LogOut className="h-[18px] w-[18px]" />
          {t("nav.logout")}
        </button>
      </aside>
      <div className="flex-1 lg:ml-60 flex flex-col min-w-0">
        <header className="sticky top-0 z-20 glass-topbar border-b border-slate-200 px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="font-mono text-xs uppercase tracking-wider text-slate-500 truncate" data-testid="shop-slug-label">
            {shop?.slug}
          </div>
          <div className="flex items-center gap-3">
            {active && (
              <button
                data-testid="alerts-bell"
                onClick={() => navigate("/app/alerts")}
                className="relative p-2 rounded-full hover:bg-slate-100 transition-colors"
              >
                <BellRing className="h-5 w-5 text-slate-600" />
                {alertCount > 0 && (
                  <span data-testid="alerts-count-badge" className="absolute -top-0.5 -right-0.5 bg-red-600 text-white text-[10px] font-bold rounded-full h-4.5 min-w-[18px] h-[18px] flex items-center justify-center px-1">
                    {alertCount}
                  </span>
                )}
              </button>
            )}
            <LanguageSwitcher />
            <div className="text-sm text-slate-600" data-testid="user-email-label">{user?.email}</div>
          </div>
        </header>
        {active && (
          <nav className="lg:hidden sticky top-16 z-20 px-3 py-2 flex gap-1 overflow-x-auto" style={{ backgroundColor: "color-mix(in srgb, var(--brand, #1E3A5F) 30%, #0f172a)" }} data-testid="mobile-nav">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                className={({ isActive }) =>
                  `flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap shrink-0 ${
                    isActive ? "bg-emerald-600 text-white" : "text-slate-300"
                  }`
                }
              >
                <l.icon className="h-4 w-4" />
                {l.label}
              </NavLink>
            ))}
            <button onClick={async () => { await logout(); navigate("/login"); }}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap shrink-0 text-slate-300">
              <LogOut className="h-4 w-4" />{t("nav.logout")}
            </button>
          </nav>
        )}
        <main className="p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
